import React, { useEffect, useState }     from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useController } from '@rest-hooks/react';

import OrganizationResource from 'resources/organization/OrganizationResource';
import OrganizationThemeResource from 'resources/organization/ThemeResource';
import OrganizationElementResource from 'resources/organization/PageElementResource';

import PublicThemeResource from 'resources/public/ThemeResource';
import RewardResource from "resources/organization/RewardResource";

import Layout             from 'components/layouts/index';
import CrowdclassFallback from 'components/CrowdclassFallback';

import OrganizationEditor from './OrganizationEditor';
import createDetailsFormData from './OrganizationEditor/createDetailsFormData';
import createThemeFormData from './OrganizationEditor/createThemeFormData';
import handleValidation from './OrganizationEditor/handleValidation';

import Preview from './Preview';
import FloatingActions from './FloatingActions';
import LinksEditor from './LinksEditor';
import PreviewModal from './PreviewModal';
import PublishModal from './PublishModal';
import { phoneFrameStyles } from './phoneFrameStyles';

import { validateEmail } from 'utils/validations/validateEmail';
import { useToast } from 'utils/context/ToastContext';
import { useError } from 'utils/useErrorController';
import { activePasses } from 'utils/filterFunctions'; 
import AuthToken from "utils/localStorage/AuthToken";
import CoursesList from "utils/localStorage/CoursesList";
import UserName from "utils/localStorage/UserName";

import { Grid, Box } from '@mui/material';

const pageInfo = {
  name: "Club Profile & Link-In-Bio",
  description: "Customize your Club Profile elements and Link In-Bio page and share it with your audience",
};

const OrganizationSettingsV2 = () => {

  const { fetch } = useController();
  const { setLoading, validate, handleError } = useError();
  const { showToast } = useToast();
  
  const params = useParams();
  const organizationId = parseInt(params.organizationId);

  const [organization, setOrganization] = useState({});
  const [originalOrganization, setOriginalOrganization] = useState({});
  const [socials, setSocials] = useState([]);
  const [links, setLinks] = useState([]);
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [openPublishModal, setOpenPublishModal] = useState(false);
  const [passes, setPasses] = useState([]);
  
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  
  const fetchOrganizationWithTheme = async () => {
    try {
      const themesArray = await fetch(PublicThemeResource.list(), { id: organizationId });

      const { highlight_color, custom_cta, custom_cta_action, primary_text_color, secondary_text_color, primary_background_color, border_color } = themesArray[0]?.published_theme?.theme || {};

      const theme = primary_text_color === 'white' ? 'dark' : 'light';

      const organizationData = await fetch(OrganizationResource.detail(), { id: organizationId });

      const newOrganization = { 
        ...organizationData, 
        highlight_color, 
        custom_cta, 
        custom_cta_action, 
        theme,
        primary_text_color,
        secondary_text_color,
        primary_background_color,
        border_color
      };

      setOrganization(newOrganization);
      setOriginalOrganization(newOrganization);
    } catch (error) {
      handleError(error);
    }
  };

  const fetchOrganizationElements = async () => {
    try {
      const { socials, links } = await fetch(OrganizationElementResource.detail(), { organization_id: organizationId, id: 1 });
      setSocials(socials);
      setLinks(links);
    } catch (error) {
      handleError(error);
    }
  };

  const fetchPasses = async () => {
    try {
      const rewards = await fetch(RewardResource.list(), { organization_id: organizationId, });
      const passes = rewards.filter(activePasses);
      setPasses(passes);
    } catch (error) {
      handleError(error);
    }
  };

  const initialize = async () => {
    await Promise.all([
      fetchOrganizationWithTheme(),
      fetchOrganizationElements(),
      fetchPasses()
    ]);
  };

  const handleOrganizationDetailsAndThemeSave = () => {
    fetchOrganizationWithTheme();
  };

  const handleLinksEditorSave = () => {
    fetchOrganizationElements();
  };

  const handleSocialsEditorSave = () => {
    fetchOrganizationElements();
  };
 
  useEffect(() => {
    initialize();
  }, []);

  const handleDiscardChanges = () => {
    setUnsavedChanges(false);
    setOrganization(originalOrganization);
  };

  const handleSubmit = async () => {
    try {
      validate(organization, handleValidation);

      const formData = createDetailsFormData(organization);
      await fetch(OrganizationResource.update(), { id: organization.id }, formData );

      const { highlight_color, custom_cta, custom_cta_action, theme } = organization;

      if (highlight_color || custom_cta || custom_cta_action || theme) {
        
        const data = {};
        if (highlight_color) data["highlight_color"] = highlight_color;
        if (custom_cta) data["custom_cta"] = custom_cta;
        if (custom_cta_action) data["custom_cta_action"] = custom_cta_action;
        if (theme) data["theme"] = theme;

        const themeFormData = createThemeFormData(data);
        await fetch(OrganizationThemeResource.update(), {organization_id: organization.id}, themeFormData);
        //updates draft theme to final
        await fetch(OrganizationThemeResource.create(), {organization_id: organization.id}, {});
      }

      handleOrganizationDetailsAndThemeSave();
      showToast('success', `Organization Details Updated`);
      setLoading(false);
      setUnsavedChanges(false);

    } catch (error) {
      handleError(error);
    }
  };

  const handlePublish = async (email) => {
    try {
      setLoading(true);
      validateEmail(email, null, "Please provide a valid email address");
      const { auth_token, user_name, courses } = await fetch(OrganizationResource.update(), { id: organization.id }, { email });

      AuthToken.write(auth_token);
      CoursesList.write(courses);
      UserName.write(user_name);

      fetchOrganizationWithTheme();

      showToast('success', `Organization Published`);
      setLoading(false);
      setUnsavedChanges(false);
      setOpenPublishModal(false);
      
    } catch (error) {
      handleError(error);
    }
  };
  
  const loading = Object.keys(organization).length === 0;
  const isUnpublished = organization?.onboarding === "processed";
  const showFloatingActions = unsavedChanges || isUnpublished;

  return (
    <>
      {loading ? (
        <CrowdclassFallback />
      ) : (
        <>
          <Layout context='teacher'
                  activeMenuItem='profile'
                  pageInfo={pageInfo}
                  isUnpublished={isUnpublished}
                  v2 >
        
          <Grid container spacing={3} sx={{ minHeight: "calc(100vh - 250px)" }}>
            <Grid item xs={12} md={8} sx={{ display: "flex", flexDirection: "column", height: "100%", gap: 2 }}>

              <OrganizationEditor organization={organization}
                                  onOrganizationChange={setOrganization}
                                  onPreviewModalOpen={() => {setOpenPreviewModal(true)}}
                                  originalOrganization={originalOrganization}
                                  socials={socials}
                                  onOrganizationDetailsAndThemeSave={handleOrganizationDetailsAndThemeSave}
                                  onSocialsEditorSave={handleSocialsEditorSave}
                                  onUnsavedChangesChange={setUnsavedChanges} />

              <LinksEditor links={links} passes={passes} onSave={handleLinksEditorSave} />

            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{
                display: 'flex',  
                flexDirection: 'column',
                top: '24px',
                position: 'sticky',
                ...phoneFrameStyles
              }} >
                <Preview originalOrganizationSlug={originalOrganization.slug} organization={organization} socials={socials} links={links} />
              </Box>
            </Grid>
            </Grid>
          </Layout>

          {showFloatingActions && (
            <FloatingActions
              onSubmit={handleSubmit}
              onDiscard={handleDiscardChanges}
              onPublish={() => setOpenPublishModal(true)}
              unsavedChanges={unsavedChanges}
            />
          )}

          <PublishModal open={openPublishModal} onClose={() => setOpenPublishModal(false)} onPublish={handlePublish} />
          <PreviewModal open={openPreviewModal} onClose={() => setOpenPreviewModal(false)} originalOrganizationSlug={originalOrganization.slug} organization={organization} socials={socials} links={links}/>
        </>
      )}
    </>
  );
};

OrganizationSettingsV2.propTypes = {
  match: PropTypes.object
};

export default OrganizationSettingsV2;